import { Suspense, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { InAppErrorMessageFallback } from '../../../components/ErrorBoundary/InAppErrorMessageFallback';
import { teamAccessMessages } from '../../../components/ErrorBoundary/messages';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getApprovalActionLabel } from '../../../utils/approvalActionLabels';
import { getApprovalProductLabel } from '../../../utils/approvalProductLabels';
import { ROUTE_SECURITY } from '../utils/constants';
import { useApprovalRuleEdits } from '../utils/useApprovalRuleEdits';
import { useApprovalRulesData } from '../utils/useApprovalRulesData';

import { ApprovalProductSection } from './components/ApprovalProductSection';
import { ApprovalRulesScope } from './components/ApprovalRulesScope';
import { ApprovalRulesSkeleton } from './components/ApprovalRulesSkeleton';

const messages = defineMessages({
  backLink: {
    id: 'Security.approvalRules.backLink',
    defaultMessage: 'Security',
    description: 'Back link label from the approval rules page to the Security page',
  },
  backLinkAccessibilityLabel: {
    id: 'Security.approvalRules.backLinkAccessibilityLabel',
    defaultMessage: 'Back to Security',
    description: 'Accessibility label for navigating back to the Security page',
  },
  title: {
    id: 'Security.approvalRules.title',
    defaultMessage: 'Approval rules',
    description: 'Title for the workspace Approval rules page',
  },
  noOrgSelectedTitle: {
    id: 'Security.approvalRules.noOrgSelectedTitle',
    defaultMessage: 'No organization selected',
    description: 'Title shown when no organization is selected on the Approval rules page',
  },
  noOrgSelectedDescription: {
    id: 'Security.approvalRules.noOrgSelectedDescription',
    defaultMessage: 'Select an organization to manage approval rules.',
    description: 'Description shown when no organization is selected on the Approval rules page',
  },
  description: {
    id: 'Security.approvalRules.description',
    defaultMessage: 'Require approvals for sensitive actions (eg. transfers, changes)',
    description: 'Description shown on the Approval rules page',
  },
});

function ApprovalRulesContent() {
  const { formatMessage } = useIntl();
  const { selectedOrganization } = useOrganization();
  const legalEntities = selectedOrganization?.legalEntities ?? [];
  const showScope = legalEntities.length > 1;

  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState(
    legalEntities[0]?.legalEntityId ?? '',
  );
  const { approvalProducts, members } = useApprovalRulesData({
    legalEntityId: selectedLegalEntityId,
  });

  const {
    ruleStateById,
    maxRequiredApprovalsByRuleId,
    handleToggle,
    handleApproversChange,
    handleCountChange,
    resetEdits,
  } = useApprovalRuleEdits(approvalProducts, members);

  const handleLegalEntityChange = useCallback(
    (legalEntityId: string) => {
      setSelectedLegalEntityId(legalEntityId);
      resetEdits();
    },
    [resetEdits],
  );

  return (
    <VStack gap={6} width="100%">
      {showScope ? (
        <ApprovalRulesScope
          legalEntities={legalEntities}
          selectedLegalEntityId={selectedLegalEntityId}
          onLegalEntityChange={handleLegalEntityChange}
        />
      ) : null}
      {approvalProducts.map((product, index) => (
        <VStack key={product.product} gap={6} width="100%">
          <ApprovalProductSection
            productName={getApprovalProductLabel(product.product, formatMessage)}
            showColumnHeaders={index === 0}
            rules={product.rules.map((rule) => ({
              id: rule.id,
              ruleName: getApprovalActionLabel(rule.action, formatMessage),
              maxRequiredApprovals: maxRequiredApprovalsByRuleId[rule.id] ?? 0,
              approverOptions: rule.approverOptions,
              ...ruleStateById[rule.id],
              onToggle: () => handleToggle(rule.id),
              onApproversChange: (value) => handleApproversChange(rule.id, value),
              onCountChange: (count) => handleCountChange(rule.id, count),
            }))}
          />
          {index < approvalProducts.length - 1 ? <Divider /> : null}
        </VStack>
      ))}
    </VStack>
  );
}

export function ApprovalRulesPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { selectedOrganization, hasRequestedOrganizationAccessDenied } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';

  const handleBackClick = useCallback(() => {
    navigate(ROUTE_SECURITY);
  }, [navigate]);

  if (hasRequestedOrganizationAccessDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
      />
    );
  }

  if (!organizationId) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.noOrgSelectedTitle)}
        description={formatMessage(messages.noOrgSelectedDescription)}
      />
    );
  }

  return (
    <VStack
      className="workspaceContentFadeIn"
      gap={6}
      width="100%"
      data-testid="approval-rules-page"
    >
      <VStack gap={4} width="100%">
        <Pressable
          background="transparent"
          noScaleOnPress
          onClick={handleBackClick}
          accessibilityLabel={formatMessage(messages.backLinkAccessibilityLabel)}
        >
          <HStack alignItems="center" gap={2}>
            <Icon name="caretLeft" size="s" color="fgMuted" />
            <Text as="span" font="label1" color="fgMuted">
              {formatMessage(messages.backLink)}
            </Text>
          </HStack>
        </Pressable>
        <VStack gap={1} width="100%">
          <Text as="h1" font="title1">
            {formatMessage(messages.title)}
          </Text>
          <Text font="label2" color="fgMuted">
            {formatMessage(messages.description)}
          </Text>
        </VStack>
      </VStack>

      <Suspense fallback={<ApprovalRulesSkeleton />}>
        <ApprovalRulesContent key={organizationId} />
      </Suspense>
    </VStack>
  );
}
